// Opportunities (Blue Market only): the pipeline board across clients, a client's Opportunities tab, and the editor.
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DndContext, DragOverlay, PointerSensor, TouchSensor, closestCorners, useDroppable, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { del, get, patch, post } from '../api';
import { useAuth } from '../auth';
import { Icon, Modal, formatDate, initials, useConfirm, useToast } from '../ui';
import type { Client, OppStage, Opportunity, Project } from '../types';
import { Page, useCrumbs } from './Shell';

export const STAGES: { key: OppStage; label: string }[] = [
  { key: 'qualifying', label: 'Qualifying' }, { key: 'proposal', label: 'Proposal' }, { key: 'negotiation', label: 'Negotiation' },
  { key: 'won', label: 'Won' }, { key: 'lost', label: 'Lost' },
];
const LABEL = Object.fromEntries(STAGES.map((s) => [s.key, s.label])) as Record<OppStage, string>;
export const rand = (cents: number | null | undefined, short = false) => {
  if (cents == null) return '–';
  const r = cents / 100;
  if (short && r >= 1_000_000) return `R ${(r / 1_000_000).toFixed(r >= 10_000_000 ? 0 : 1)}m`;
  if (short && r >= 10_000) return `R ${Math.round(r / 1000)}k`;
  return `R ${r.toLocaleString('en-ZA', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};
const sum = (list: Opportunity[], weighted = false) => list.reduce((n, o) => n + (o.value_cents ?? 0) * (weighted ? o.probability / 100 : 1), 0);

interface PipelineData { opportunities: Opportunity[]; people: { id: string; name: string }[]; can_edit: boolean }

function OppCard({ o, onOpen, dragging }: { o: Opportunity; onOpen?: () => void; dragging?: boolean }) {
  const late = !o.closed_at && o.expected_close && o.expected_close < new Date().toISOString().slice(0, 10);
  return (
    <div className={`tcard opp ${dragging ? 'dragging' : ''}`} onClick={onOpen} role="button" tabIndex={-1}>
      <div className="tcard-title"><span>{o.title}</span></div>
      <div className="muted tiny">{o.client?.name}</div>
      <div className="tcard-meta">
        <b className="small">{rand(o.value_cents, true)}</b>
        {!o.closed_at && <span className="muted tiny">{o.probability}%</span>}
        {o.expected_close && !o.closed_at && <span className={`due ${late ? 'late' : ''}`}><Icon name="calendar" className="ico xs" />{new Date(o.expected_close + 'T00:00').toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}</span>}
        {o.stage === 'won' && o.project_id && <span className="badge ok tiny-badge">Project</span>}
        <span className="grow" />
        {o.owner && <span className="avatar xs staff" title={o.owner.name}>{initials(o.owner.name)}</span>}
      </div>
    </div>
  );
}

function SortableOpp({ o, onOpen, disabled }: { o: Opportunity; onOpen: () => void; disabled: boolean }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: o.id, disabled });
  return <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.35 : 1 }} {...attributes} {...listeners}><OppCard o={o} onOpen={onOpen} /></div>;
}

function Column({ id, children, className }: { id: string; children: React.ReactNode; className: string }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return <div ref={setNodeRef} className={`${className} ${isOver ? 'over' : ''}`}>{children}</div>;
}

export function PipelinePage() {
  const { me } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const setCrumbs = useCrumbs();
  useEffect(() => setCrumbs([{ label: 'Pipeline' }]), [setCrumbs]);
  const { data } = useQuery({ queryKey: ['pipeline'], queryFn: () => get<PipelineData>('/api/pipeline') });
  const [owner, setOwner] = useState('');
  const [open, setOpen] = useState<Opportunity | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [losing, setLosing] = useState<{ id: string; after?: string; before?: string } | null>(null);
  const [won, setWon] = useState<Opportunity | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }), useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 6 } }));
  const list = useMemo(() => (data?.opportunities ?? []).filter((o) => !owner || o.owner?.id === owner), [data, owner]);

  const move = async (id: string, stage: OppStage, after?: string, before?: string, lost_reason = '') => {
    qc.setQueryData<PipelineData>(['pipeline'], (d) => d && { ...d, opportunities: d.opportunities.map((o) => o.id === id ? { ...o, stage } : o) });
    try {
      const o = await post<Opportunity>(`/api/opportunities/${id}/move`, { stage, after_id: after, before_id: before, lost_reason });
      if (stage === 'won' && !o.project_id) setWon(o);
    } catch (e) { toast((e as Error).message, true); }
    qc.invalidateQueries({ queryKey: ['pipeline'] });
  };
  const onDragEnd = (e: DragEndEvent) => {
    setActiveId(null);
    const id = String(e.active.id), over = e.over ? String(e.over.id) : null;
    if (!over || over === id || !data) return;
    const overOpp = data.opportunities.find((o) => o.id === over);
    const stage = (overOpp ? overOpp.stage : over.replace('stage:', '')) as OppStage;
    const col = list.filter((o) => o.stage === stage && o.id !== id);
    const i = overOpp ? col.findIndex((o) => o.id === over) : col.length;
    const after = col[i - 1]?.id, before = overOpp ? col[i]?.id : undefined;
    const current = data.opportunities.find((o) => o.id === id);
    if (stage === 'lost' && current?.stage !== 'lost') { setLosing({ id, after, before }); return; }
    move(id, stage, after, before);
  };
  if (!data) return <main className="page"><span className="spin muted" /></main>;
  const openOnes = list.filter((o) => !o.closed_at);
  const active = activeId ? data.opportunities.find((o) => o.id === activeId) : null;
  return (
    <Page title="Pipeline" actions={
      <select className="input compact" value={owner} onChange={(e) => setOwner(e.target.value)} aria-label="Owner">
        <option value="">Everyone's</option>{data.people.map((p) => <option key={p.id} value={p.id}>{p.id === me?.id ? 'Mine' : p.name}</option>)}
      </select>}>
      <div className="pipe-summary">
        <span><span className="muted small">Open</span> <b>{rand(sum(openOnes))}</b></span>
        <span><span className="muted small">Weighted</span> <b>{rand(Math.round(sum(openOnes, true)))}</b></span>
        <span><span className="muted small">Won in the last 90 days</span> <b>{rand(sum(list.filter((o) => o.stage === 'won')))}</b></span>
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={(e) => setActiveId(String(e.active.id))} onDragEnd={onDragEnd} onDragCancel={() => setActiveId(null)}>
        <div className="board pipeline">
          {STAGES.map((s) => {
            const col = list.filter((o) => o.stage === s.key);
            return (
              <Column key={s.key} id={`stage:${s.key}`} className={`column stage-${s.key}`}>
                <div className="column-head"><span className="column-name">{s.label}</span><span className="muted tiny">{col.length}</span><span className="grow" /><span className="tiny"><b>{rand(sum(col), true)}</b></span></div>
                <SortableContext items={col.map((o) => o.id)} strategy={verticalListSortingStrategy}>
                  <div className="column-body">
                    {col.map((o) => <SortableOpp key={o.id} o={o} onOpen={() => setOpen(o)} disabled={!data.can_edit} />)}
                    {!col.length && <div className="column-empty">{s.key === 'won' || s.key === 'lost' ? 'None in the last 90 days' : 'Nothing here'}</div>}
                  </div>
                </SortableContext>
              </Column>
            );
          })}
        </div>
        <DragOverlay>{active ? <OppCard o={active} dragging /> : null}</DragOverlay>
      </DndContext>
      {!data.opportunities.length && <p className="muted small">No opportunities yet. Add one from a client's Opportunities tab.</p>}
      {open && <OpportunityDialog clientId={open.client_id} opp={open} people={data.people} onClose={() => setOpen(null)} />}
      {losing && <LostDialog onClose={() => setLosing(null)} onSave={(reason) => { move(losing.id, 'lost', losing.after, losing.before, reason); setLosing(null); }} />}
      {won && <WonDialog opp={won} onClose={() => setWon(null)} onMade={(p) => { setWon(null); navigate(`/projects/${p.id}`); }} />}
    </Page>
  );
}

function LostDialog({ onClose, onSave }: { onClose: () => void; onSave: (reason: string) => void }) {
  const [reason, setReason] = useState('');
  return (
    <Modal title="Why was it lost?" onClose={onClose}>
      <form className="stack" onSubmit={(e) => { e.preventDefault(); if (reason.trim()) onSave(reason.trim()); }}>
        <input className="input" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} autoFocus placeholder="e.g. Budget moved to next year, chose an in-house team" />
        <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}><button type="button" className="btn ghost" onClick={onClose}>Cancel</button><button className="btn primary" disabled={!reason.trim()}>Mark as lost</button></div>
      </form>
    </Modal>
  );
}

function WonDialog({ opp, onClose, onMade }: { opp: Opportunity; onClose: () => void; onMade: (p: Project) => void }) {
  const { can } = useAuth();
  const toast = useToast();
  const [name, setName] = useState(opp.title);
  const make = useMutation({ mutationFn: () => post<Project>(`/api/opportunities/${opp.id}/project`, { name }), onSuccess: onMade, onError: (e: Error) => toast(e.message, true) });
  return (
    <Modal title="Won 🎉" onClose={onClose}>
      <div className="stack">
        <p className="small" style={{ margin: 0 }}>{opp.title} for {opp.client?.name ?? 'the client'}{opp.value_cents ? `, ${rand(opp.value_cents)}` : ''}.</p>
        {can('delivery.edit') ? <>
          <label className="field"><span>Start the project now?</span><input className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} /></label>
          <p className="muted tiny" style={{ margin: 0 }}>Creates the project and its folder{opp.client && ['lead', 'prospect'].includes(opp.client.stage) ? `, and makes ${opp.client.name} an active client` : ''}.</p>
          <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}><button className="btn ghost" onClick={onClose}>Later</button>
            <button className="btn primary" disabled={!name.trim() || make.isPending} onClick={() => make.mutate()}>{make.isPending && <span className="spin" />}Create project</button></div>
        </> : <div className="row" style={{ justifyContent: 'flex-end' }}><button className="btn" onClick={onClose}>Close</button></div>}
      </div>
    </Modal>
  );
}

// ---------- a client's Opportunities tab ----------

export function OpportunitiesTab({ client }: { client: Client }) {
  const { can } = useAuth();
  const { data } = useQuery({ queryKey: ['opportunities', client.id], queryFn: () => get<PipelineData>(`/api/clients/${client.id}/opportunities`) });
  const [editing, setEditing] = useState<Opportunity | 'new' | null>(null);
  const openOnes = (data?.opportunities ?? []).filter((o) => !o.closed_at), closed = (data?.opportunities ?? []).filter((o) => o.closed_at);
  const row = (o: Opportunity) => (
    <button key={o.id} className="contact-row" onClick={() => setEditing(o)}>
      <span className="grow" style={{ minWidth: 0 }}>
        <span className="row wrap" style={{ gap: 6 }}><b className="small">{o.title}</b><span className={`badge tiny-badge stage-badge-${o.stage}`}>{LABEL[o.stage]}</span>
          {o.project_id && <Link to={`/projects/${o.project_id}`} className="badge ok tiny-badge" onClick={(e) => e.stopPropagation()}>Project</Link>}</span>
        <span className="muted tiny">{[o.closed_at ? `${o.stage === 'won' ? 'Won' : 'Lost'} ${formatDate(o.closed_at)}` : o.expected_close ? `Expected ${formatDate(o.expected_close)}` : null,
                                     o.stage === 'lost' ? o.lost_reason : null, o.owner?.name].filter(Boolean).join(' · ')}</span>
      </span>
      <span className="opp-value"><b className="small">{rand(o.value_cents)}</b>{!o.closed_at && <span className="muted tiny">{o.probability}%</span>}</span>
    </button>
  );
  return (
    <section className="card">
      <div className="card-head"><h2>Opportunities</h2><span className="muted tiny">{openOnes.length ? `${rand(sum(openOnes))} open` : ''}</span><span className="grow" />
        {can('crm.edit') && <button className="btn sm" onClick={() => setEditing('new')}>Add an opportunity</button>}</div>
      {openOnes.map(row)}
      {closed.length > 0 && <div className="list-label">Closed</div>}
      {closed.map(row)}
      {data && !data.opportunities.length && <div className="empty">No opportunities yet. Add potential work with {client.name} to track it in the pipeline.</div>}
      {editing && <OpportunityDialog clientId={client.id} opp={editing === 'new' ? null : editing} people={data?.people ?? []} onClose={() => setEditing(null)} />}
    </section>
  );
}

function OpportunityDialog({ clientId, opp, people, onClose }: { clientId: string; opp: Opportunity | null; people: { id: string; name: string }[]; onClose: () => void }) {
  const { me, can } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { confirm, dialog } = useConfirm();
  const edit = can('crm.edit');
  const [f, setF] = useState({ title: opp?.title ?? '', value: opp?.value_cents != null ? String(opp.value_cents / 100) : '', stage: opp?.stage ?? 'qualifying' as OppStage,
                               probability: opp ? String(opp.probability) : '', expected_close: opp?.expected_close ?? '', owner_id: opp?.owner?.id ?? me?.id ?? '',
                               notes: opp?.notes ?? '', lost_reason: opp?.lost_reason ?? '' });
  const [won, setWon] = useState<Opportunity | null>(null);
  const refresh = () => { qc.invalidateQueries({ queryKey: ['pipeline'] }); qc.invalidateQueries({ queryKey: ['opportunities', clientId] }); };
  const save = useMutation({
    mutationFn: () => {
      const value = f.value.trim() ? Math.round(Number(f.value.replace(/[\s,R]/g, '')) * 100) : null;
      const body = { title: f.title, value_cents: value, stage: f.stage, expected_close: f.expected_close || null, owner_id: f.owner_id || null, notes: f.notes,
                     lost_reason: f.lost_reason, ...(f.probability !== '' && (!opp || f.stage === opp.stage) ? { probability: Number(f.probability) } : {}) };
      return opp ? patch<Opportunity>(`/api/opportunities/${opp.id}`, body) : post<Opportunity>(`/api/clients/${clientId}/opportunities`, body);
    },
    onSuccess: (o) => { refresh(); if (o.stage === 'won' && !o.project_id && opp?.stage !== 'won') setWon(o); else onClose(); },
    onError: (e: Error) => toast(e.message, true),
  });
  const remove = async () => {
    if (!(await confirm(`Delete "${opp!.title}"?`, { body: 'It disappears from the pipeline and the reports. Usually it is better to mark it as lost.', ok: 'Delete', danger: true }))) return;
    try { await del(`/api/opportunities/${opp!.id}`); refresh(); onClose(); } catch (e) { toast((e as Error).message, true); }
  };
  if (won) return <WonDialog opp={won} onClose={onClose} onMade={(p) => navigate(`/projects/${p.id}`)} />;
  return (
    <Modal title={opp ? opp.title : 'Add an opportunity'} onClose={onClose} width={560}>
      <form className="stack" onSubmit={(e) => { e.preventDefault(); if (f.title.trim()) save.mutate(); }}>
        <label className="field"><span>What it is</span><input className="input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} maxLength={200} required autoFocus={!opp} disabled={!edit} placeholder="e.g. Loyalty app rebuild" /></label>
        <div className="grid-fields">
          <label className="field"><span>Value (R)</span><input className="input" inputMode="decimal" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} disabled={!edit} placeholder="850 000" /></label>
          <label className="field"><span>Stage</span>
            <select className="input" value={f.stage} onChange={(e) => setF({ ...f, stage: e.target.value as OppStage, probability: '' })} disabled={!edit}>
              {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </label>
          <label className="field"><span>Probability (%)</span><input className="input" type="number" min={0} max={100} value={f.probability} placeholder="stage default" onChange={(e) => setF({ ...f, probability: e.target.value })} disabled={!edit} /></label>
          <label className="field"><span>Expected close</span><input className="input" type="date" value={f.expected_close} onChange={(e) => setF({ ...f, expected_close: e.target.value })} disabled={!edit} /></label>
          <label className="field"><span>Owner</span>
            <select className="input" value={f.owner_id} onChange={(e) => setF({ ...f, owner_id: e.target.value })} disabled={!edit}>
              <option value="">Nobody</option>{people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
        </div>
        {f.stage === 'lost' && <label className="field"><span>Why it was lost</span><input className="input" value={f.lost_reason} onChange={(e) => setF({ ...f, lost_reason: e.target.value })} maxLength={300} required disabled={!edit} /></label>}
        <label className="field"><span>Notes</span><textarea className="input" rows={3} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} maxLength={10000} disabled={!edit} /></label>
        <div className="row wrap" style={{ gap: 8 }}>
          {opp && edit && <button type="button" className="btn ghost sm danger" onClick={remove}>Delete</button>}
          {opp?.stage === 'won' && !opp.project_id && can('delivery.edit') && <button type="button" className="btn sm" onClick={() => setWon(opp)}>Create project…</button>}
          {opp?.project_id && <Link to={`/projects/${opp.project_id}`} className="btn sm ghost">Open the project</Link>}
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>{edit ? 'Cancel' : 'Close'}</button>
          {edit && <button className="btn primary" disabled={!f.title.trim() || save.isPending}>{save.isPending && <span className="spin" />}Save</button>}
        </div>
        {opp && <p className="muted tiny" style={{ margin: 0 }}>Added by {opp.created_by} · {formatDate(opp.created_at)}{opp.client ? <> · <Link to={`/clients/${opp.client.id}?tab=opportunities`}>{opp.client.name}</Link></> : null}</p>}
      </form>
      {dialog}
    </Modal>
  );
}
